/**
 * OrderPrepCard — two-stage Prep Order → Push to Vendor card.
 *
 * Drop-in for Job Detail (classic + cockpit) and Maverick. Reuses the
 * canonical POOrderingModal in "prep" or "push" mode.
 *
 * Render rules:
 *  • Hidden once materials are received/staged/loaded.
 *  • Shows pre-spend gate banner when blocked.
 *  • Shows draft summary + Push CTA when a "prepared" draft exists.
 *  • Shows void reason banner when the draft was voided (CO or integrity).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useOrderDraft } from "@/data/OrderDraftContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { derivePreSpendGate } from "@/data/preSpendGate";
import {
  collectBomItems,
  summarizeDraft,
  validateDraftForPush,
} from "@/data/orderDraft";
import { POOrderingModal, type POOrderingMode, type WorkOrderItem } from "@/components/POOrderingModal";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ClipboardCheck, Send, AlertTriangle, ShieldAlert, RefreshCcw, Lock,
} from "lucide-react";

interface Props {
  jobId: string;
  /** Optional className for layout integration */
  className?: string;
}

export function OrderPrepCard({ jobId, className }: Props) {
  const { jobs } = useAppData();
  const { getDraft } = useOrderDraft();
  const fin = useFinancialPermissions();
  const canViewFinancials = fin.viewFinancialSummary;
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<POOrderingMode>("prep");

  const job = useMemo(() => jobs.find((j) => j.id === jobId), [jobs, jobId]);
  const draft = getDraft(jobId);
  const gate = useMemo(() => job ? derivePreSpendGate(job) : null, [job]);

  const workOrderItems: WorkOrderItem[] = useMemo(() => {
    if (!job) return [];
    return collectBomItems(job).map((b) => ({ sku: b.sku, quantity: b.quantity }));
  }, [job]);

  // Hide once materials are advanced past ordering.
  const advancedReadiness = ["Fully Received", "Staged for Job", "Loaded for Crew"];
  if (!job || advancedReadiness.includes(job.materialReadiness as string)) return null;

  const jobLabel = `${job.customerName} – ${job.projectType}`;
  const pushValidation = draft && job ? validateDraftForPush(draft, job) : null;
  const summary = draft ? summarizeDraft(draft) : null;

  const openPrep = () => { setMode("prep"); setOpen(true); };
  const openPush = () => { setMode("push"); setOpen(true); };

  const isPrepared = draft?.status === "prepared";
  const isVoided = draft?.status === "voided";
  const isPushed = draft?.status === "pushed";

  return (
    <Card className={`shadow-none ${className || ""}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Order Preparation</h3>
            {isPrepared && <Badge variant="outline" className="text-[10px]">Draft Ready</Badge>}
            {isPushed && <Badge className="text-[10px] bg-success/15 text-success border-success/30">Pushed</Badge>}
            {isVoided && <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30">Voided</Badge>}
          </div>
          {gate && !gate.cleared && (
            <span className="text-[10px] text-warning flex items-center gap-1">
              <Lock className="h-3 w-3" /> {gate.shortReason}
            </span>
          )}
        </div>

        {/* Voided banner */}
        {isVoided && draft && (
          <div className={`rounded-md border p-2 mb-2 text-[11px] ${
            draft.voidedReason === "integrity_violation"
              ? "bg-destructive/10 border-destructive/30 text-destructive"
              : "bg-warning/10 border-warning/30 text-warning"
          }`}>
            <p className="flex items-center gap-1 font-medium">
              {draft.voidedReason === "integrity_violation"
                ? <ShieldAlert className="h-3 w-3" />
                : <RefreshCcw className="h-3 w-3" />}
              {draft.voidedReason === "change_order"
                ? "Draft voided by Change Order — re-prep required."
                : draft.voidedReason === "integrity_violation"
                  ? "Draft voided — BOM integrity violation logged."
                  : "Draft voided."}
            </p>
          </div>
        )}

        {/* Prepared draft summary */}
        {isPrepared && summary && draft && (
          <div className="bg-muted/40 rounded-md p-3 mb-2 text-[11px] space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Vendors</span>
              <span className="font-semibold">{summary.vendorCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Items</span>
              <span className="font-semibold">{summary.itemCount}</span>
            </div>
            {canViewFinancials && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total</span>
                <span className="font-semibold font-mono">${summary.totalCost.toLocaleString()}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Prepared by</span>
              <span>{draft.preparedBy}</span>
            </div>
          </div>
        )}

        {/* Pushed summary */}
        {isPushed && draft && (
          <div className="text-[11px] text-muted-foreground mb-2">
            Pushed by <span className="font-medium text-foreground">{draft.pushedBy}</span> · {draft.pushedPoIds?.length || 0} PO(s) created
          </div>
        )}

        {/* Push validation warning (when prepared but blocked) */}
        {isPrepared && pushValidation && !pushValidation.ok && pushValidation.reason?.kind === "gate_blocked" && (
          <div className="text-[10px] text-warning flex items-center gap-1 mb-2">
            <AlertTriangle className="h-3 w-3" />
            Push will be blocked: {pushValidation.reason.gate.shortReason}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          {!draft || isVoided ? (
            <Button
              size="sm"
              className="h-8 text-xs gap-1 flex-1"
              onClick={openPrep}
              disabled={!gate?.cleared || workOrderItems.length === 0}
              title={
                !gate?.cleared ? gate?.shortReason :
                workOrderItems.length === 0 ? "No BOM items resolved for this job" : undefined
              }
            >
              <ClipboardCheck className="h-3 w-3" /> {isVoided ? "Re-prep Order" : "Prep Order"}
            </Button>
          ) : isPrepared ? (
            <>
              <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={openPrep}>
                <RefreshCcw className="h-3 w-3" /> Re-prep
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs gap-1 flex-1"
                onClick={openPush}
                title={pushValidation?.ok ? undefined : "Push currently blocked"}
              >
                <Send className="h-3 w-3" /> Push to Vendor{(draft.vendorGroups.length || 0) > 1 ? "s" : ""}
              </Button>
            </>
          ) : (
             <Link to="/ordering-queue" className="text-[11px] text-primary hover:underline">View in Ordering →</Link>
          )}
        </div>

        <POOrderingModal
          open={open}
          onOpenChange={setOpen}
          jobId={jobId}
          jobLabel={jobLabel}
          workOrderItems={workOrderItems}
          mode={mode}
        />
      </CardContent>
    </Card>
  );
}
